// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { DashboardFilterToApi, WorkOrdersOverTimeRes } from '@evolvemep/api-ts';
import { Badge, Card, Flex, Text, Title } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';

const getTrendIcon = (percentageChange: number, isGood: 'up' | 'down') => {
  if (percentageChange === 0) {
    return (
      <Text c="gray.4" mr={4}>
        No change
      </Text>
    );
  }
  const color = isGood === 'up' && percentageChange > 0 ? 'green.5' : 'red.5';
  return (
    <>
      <EvolveIcon icon={percentageChange <= 0 ? 'TrendingDown' : 'TrendingUp'} color={color} />
      <Text fw={600} c={color} mr={4}>
        {Math.abs(percentageChange)}%
      </Text>
    </>
  );
};

type Props = {
  params: DashboardFilterToApi;
  data: WorkOrdersOverTimeRes | null | undefined;
};

export const InsightsStats = ({ params, data }: Props) => {
  const trendInfoText = useMemo(() => {
    const timeFrame = params.timeFrame;
    if (timeFrame === 'Specific Date') {
      return 'vs same date previous year';
    }
    if (timeFrame === 'Date Range') {
      return 'vs same date range previous year';
    }
    return `vs previous ${timeFrame?.replace('Last', '').replace('This', '').replace(' ', '').toLowerCase()}`;
  }, [params.timeFrame]);

  return (
    <Flex gap="sm" mb="sm">
      <Card withBorder style={{ flex: 1 }}>
        <Text size="sm" c="gray.6">
          Completed Work Orders
        </Text>
        {data && (
          <Flex direction="column" justify="space-between">
            <Title order={1}>{data.completedWorkOrders}</Title>
            <Flex>
              {getTrendIcon(data.percentageChangeFromPreviousTimeFrameForCompletedWorkOrders, 'up')}
              <Text c="gray.6">{trendInfoText}</Text>
            </Flex>
          </Flex>
        )}
      </Card>

      <Card withBorder style={{ flex: 1 }}>
        <Text size="sm" c="gray.6">
          Late Work Orders
        </Text>
        {data && (
          <Flex direction="column" justify="space-between">
            <Flex align="center">
              <Title order={1}>{data.lateWorkOrders}</Title>
              <Badge radius={2} m="xs" color="gray" size="lg">
                {data.percentageOfLateVersusTotalWorkOrders}%
              </Badge>
            </Flex>
            <Flex>
              {getTrendIcon(data.percentageChangeFromPreviousTimeFrameForLateWorkOrders, 'down')}
              <Text c="gray.6">{trendInfoText}</Text>
            </Flex>
          </Flex>
        )}
      </Card>

      <Card withBorder style={{ flex: 1 }}>
        <Flex align="center">
          <Text size="sm" c="gray.6" mr={4}>
            Avg. Manufacturing Lead Time
          </Text>
          <AgGridStyleTooltip label="Time from creating the work order to production complete">
            <EvolveIcon icon="Info" size="sm" color="gray.6" />
          </AgGridStyleTooltip>
        </Flex>
        {data && (
          <Flex direction="column" justify="space-between">
            <Title order={1}>{data.averageManufacturingLeadTime} days</Title>
            <Flex>
              {getTrendIcon(data.percentageChangeFromPreviousTimeFrameForAverageManufacturingLeadTime, 'down')}
              <Text c="gray.6">{trendInfoText}</Text>
            </Flex>
          </Flex>
        )}
      </Card>

      <Card withBorder style={{ flex: 1 }}>
        <Flex align="center">
          <Text size="sm" c="gray.6" mr={4}>
            Avg. Production Lead Time
          </Text>
          <AgGridStyleTooltip openDelay={0} withArrow label="Time from production start to production complete">
            <EvolveIcon icon="Info" size="sm" color="gray.6" />
          </AgGridStyleTooltip>
        </Flex>
        {data && (
          <Flex direction="column" justify="space-between">
            <Title order={1}>{`${data.averageProductionLeadTime} days`}</Title>
            <Flex>
              {getTrendIcon(data.percentageChangeFromPreviousTimeFrameForAverageProductionLeadTime, 'down')}
              <Text c="gray.6">{trendInfoText}</Text>
            </Flex>
          </Flex>
        )}
      </Card>
    </Flex>
  );
};
